/**
 * Grading API Service
 * IMPL-20260106-OCR-GRADER-PHASE2
 * IMPL-20260106-OCR-GRADER-PHASE3: Refactored to use fetchAPI helper for auth resilience
 *
 * Handles communication with the AI grading endpoints:
 * - Presigned URL generation for direct S3 uploads
 * - Image-based grade prediction using Gemini Vision
 * - Text-based grade estimation
 * - Grade scale reference data
 */

import { fetchAPI, getStoredToken } from './api';
import { FINGERPRINT_HASH_HEADER, getFingerprintHash } from './fingerprintService';

/**
 * Get a presigned URL for direct S3 upload
 *
 * @param {string} contentType - MIME type (image/jpeg or image/png)
 * @returns {Promise<{upload_url: string, object_key: string, expires_in: number, content_type: string, max_size_bytes: number}>}
 */
export async function getUploadUrl(contentType = 'image/jpeg') {
  return fetchAPI(`/grading/upload-url?content_type=${encodeURIComponent(contentType)}`);
}

/**
 * Upload image directly to S3 using presigned URL
 *
 * @param {string} presignedUrl - The presigned URL from getUploadUrl
 * @param {File} file - The image file to upload
 * @param {string} contentType - MIME type matching the presigned URL
 * @param {function} onProgress - Optional progress callback (0-100)
 * @returns {Promise<void>}
 */
export async function uploadToS3(presignedUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed - network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

/**
 * Get CGC grade scale reference
 *
 * @returns {Promise<{scale: Array<{grade: number, label: string, description: string}>}>}
 */
export async function getGradeScale() {
  return fetchAPI('/grading/grade-scale');
}

/**
 * Validate image file before upload
 *
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function validateGradingImage(file) {
  const MAX_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Only JPEG and PNG images are supported' };
  }

  if (file.size > MAX_SIZE) {
    return { valid: false, error: 'File too large (max 50MB)' };
  }

  return { valid: true };
}

/**
 * Get grade prediction from S3-uploaded image
 *
 * @param {string} objectKey - S3 object key from upload
 * @param {string} additionalNotes - Optional condition notes
 * @returns {Promise<{grade: number, grade_label: string, confidence: number, defects_detected: Array, grade_notes: Array, method: string, suggested_condition: string}>}
 */
export async function gradeFromS3Image(objectKey, additionalNotes = null) {
  return fetchAPI('/grading/image-estimate/s3', {
    method: 'POST',
    body: JSON.stringify({
      object_key: objectKey,
      additional_notes: additionalNotes,
    }),
  });
}

/**
 * Get grade prediction from multiple S3-uploaded images (front + back cover)
 * IMPL-20260106-OCR-GRADER-PHASE3: Dual image support per PRD requirements
 *
 * @param {string[]} objectKeys - Array of S3 object keys (front cover first, back cover second)
 * @param {string} additionalNotes - Optional condition notes
 * @returns {Promise<{grade: number, grade_label: string, confidence: number, defects_detected: Array, grade_notes: Array, method: string, suggested_condition: string}>}
 */
export async function gradeFromMultipleS3Images(objectKeys, additionalNotes = null) {
  return fetchAPI('/grading/image-estimate/s3/multi', {
    method: 'POST',
    body: JSON.stringify({
      object_keys: objectKeys,
      additional_notes: additionalNotes,
    }),
  });
}

/**
 * Get grade prediction from image URL
 *
 * @param {string} imageUrl - URL of the comic image
 * @param {string} additionalNotes - Optional condition notes
 * @returns {Promise<{grade: number, grade_label: string, confidence: number, defects_detected: Array, grade_notes: Array, method: string, suggested_condition: string}>}
 */
export async function gradeFromImageUrl(imageUrl, additionalNotes = null) {
  return fetchAPI('/grading/image-estimate', {
    method: 'POST',
    body: JSON.stringify({
      image_url: imageUrl,
      additional_notes: additionalNotes,
    }),
  });
}

/**
 * Get grade prediction from uploaded file (direct upload)
 * Note: Uses raw fetch due to FormData requirements - fetchAPI sets Content-Type: 'application/json'
 *
 * @param {File} file - Image file to analyze
 * @param {string} additionalNotes - Optional condition notes
 * @returns {Promise<{grade: number, grade_label: string, confidence: number, defects_detected: Array, grade_notes: Array, method: string, suggested_condition: string}>}
 */
export async function gradeFromUploadedFile(file, additionalNotes = null) {
  // Import API_BASE for FormData upload (can't use fetchAPI due to Content-Type requirements)
  const { API_BASE } = await import('./api');

  const formData = new FormData();
  formData.append('file', file);

  const url = additionalNotes
    ? `${API_BASE}/grading/image-estimate/upload?additional_notes=${encodeURIComponent(additionalNotes)}`
    : `${API_BASE}/grading/image-estimate/upload`;

  // Build headers with auth token (don't set Content-Type - browser sets it for FormData)
  const headers = {};
  const token = getStoredToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // MAESTRO:CONACCESS P2 — anonymous path requires the fingerprint header
    // per Section 9.0 + Section 18 P2. Silently fall back to sending without
    // the header if the SDK load fails (the backend will return 401 with
    // error_code='fingerprint_header_missing').
    try {
      const hash = await getFingerprintHash();
      headers[FINGERPRINT_HASH_HEADER] = hash;
    } catch (_fpErr) {
      /* SDK load/compute failed; backend will 401 */
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    // Preserve structured detail (error_code, current, limit) so the caller
    // can route to the cap-exhaustion conversion screen.
    const payload = await response.json().catch(() => ({}));
    const err = new Error(payload.detail?.message || payload.detail || 'Failed to get grade prediction');
    err.status = response.status;
    err.detail = payload.detail || null;
    throw err;
  }

  return response.json();
}

/**
 * Get grade estimate from text condition notes
 *
 * @param {string} conditionNotes - Text description of comic condition
 * @param {number} baseGrade - Starting grade (default 10.0)
 * @returns {Promise<{grade: number, grade_label: string, confidence: number, defects_detected: Array, grade_notes: Array, method: string, suggested_condition: string}>}
 */
export async function gradeFromText(conditionNotes, baseGrade = 10.0) {
  return fetchAPI('/grading/text-estimate', {
    method: 'POST',
    body: JSON.stringify({
      condition_notes: conditionNotes,
      base_grade: baseGrade,
    }),
  });
}

/**
 * Get defect catalog reference
 *
 * @returns {Promise<{defects: Object, categories: Object}>}
 */
export async function getDefectCatalog() {
  return fetchAPI('/grading/defect-catalog');
}

/**
 * Create a product listing from an AI grading result
 * USER-CON-001 Grade First
 *
 * @param {Object} params - Product creation parameters
 * @param {number} params.grade_request_id - ID of the GradeRequest to convert
 * @param {number} params.comic_issue_id - Optional comic issue link
 * @param {string} params.title - Optional product title override
 * @param {number} params.price - Listing price
 * @param {number} params.stock - Quantity (default 1)
 * @param {string} params.notes - Seller notes
 * @returns {Promise<{product_id: number, sku: string, name: string, price: number, condition: string, predicted_grade: number, confidence: number, image_url: string}>}
 */
export async function createProductFromGrade({
  grade_request_id,
  comic_issue_id = null,
  title = null,
  price = null,
  stock = 1,
  notes = null,
}) {
  return fetchAPI('/grading/create-product', {
    method: 'POST',
    body: JSON.stringify({
      grade_request_id,
      comic_issue_id,
      title,
      price,
      stock,
      notes,
    }),
  });
}

export default {
  getUploadUrl,
  uploadToS3,
  getGradeScale,
  getDefectCatalog,
  validateGradingImage,
  gradeFromS3Image,
  gradeFromMultipleS3Images,
  gradeFromImageUrl,
  gradeFromUploadedFile,
  gradeFromText,
  createProductFromGrade,
};
